import { c as _c } from "react/compiler-runtime";
import { Stack, Typography, Divider } from '@mui/material';
import { BackupPolicyType, VaultStorageTier } from '@repo/api-gw-sdk';
import { isPitrPolicyType, isStandardPolicyType } from './backupPoliciesTypes';
import { SupportedResourceTypesText, highFrequencyPolicyResourceTypeLimitations } from './supportedResourceTypesText';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';

// hasDisclaimerContent mirrors the policy-type branches in
// BackupPolicyTypeDescription. When the description renders nothing the
// disclaimer wrapper would otherwise emit just an empty section + a
// Divider, which surfaces in the UI as a stray horizontal line directly
// beneath the wizard stepper's own Divider.
const hasDisclaimerContent = (policyType: BackupPolicyType): boolean => policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY || isStandardPolicyType(policyType) || isPitrPolicyType(policyType);
export const BackupPolicySupportedDisclaimer = t0 => {
  const $ = _c(9);
  const {
    policyType,
    vaultStorageTier
  } = t0;
  if (!hasDisclaimerContent(policyType)) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "2px"
    };
    t2 = <div />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      alignItems: "center"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== policyType || $[4] !== vaultStorageTier) {
    t4 = <Stack className="bg-(--mui-palette-background-section)" sx={t3}><BackupPolicyTypeDescription policyType={policyType} vaultStorageTier={vaultStorageTier} /></Stack>;
    $[3] = policyType;
    $[4] = vaultStorageTier;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Divider />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <Stack sx={t1}>{t2}{t4}{t5}</Stack>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
export const BackupPolicyTypeDescription = t0 => {
  const $ = _c(12);
  const {
    policyType: t1,
    showForPitr: t2,
    vaultStorageTier
  } = t0;
  const policyType = t1 === undefined ? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD : t1;
  const showForPitr = t2 === undefined ? true : t2;
  const isColdTier = vaultStorageTier === VaultStorageTier.VAULT_STORAGE_TIER_COLD;
  const t3 = useBackupPolicyAvailableTypes();
  let t4;
  if ($[0] !== policyType || $[1] !== t3 || $[2] !== vaultStorageTier) {
    t4 = t3(policyType, vaultStorageTier);
    $[0] = policyType;
    $[1] = t3;
    $[2] = vaultStorageTier;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const resourceTypes = t4;
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY || isStandardPolicyType(policyType)) {
    const policyLabel = policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ? "High-frequency" : "Standard";
    const destinationSuffix = isColdTier ? " \u2022 Storing snapshots in cold tier vault" : policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD ? " \u2022 Storing snapshots in source account" : policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES ? " \u2022 Storing snapshots in source subscription" : "";
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        padding: "12px 0px"
      };
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    const t6 = `${policyLabel} backup policy${destinationSuffix} • Available for `;
    const t7 = policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ? highFrequencyPolicyResourceTypeLimitations : undefined;
    let t8;
    if ($[5] !== resourceTypes || $[6] !== t7) {
      t8 = <SupportedResourceTypesText resourceTypes={resourceTypes} resourceTypeLimitations={t7} />;
      $[5] = resourceTypes;
      $[6] = t7;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    let t9;
    if ($[8] !== t6 || $[9] !== t8) {
      t9 = <Typography sx={t5}>{t6}{t8}</Typography>;
      $[8] = t6;
      $[9] = t8;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    return t9;
  }
  if (showForPitr && isPitrPolicyType(policyType)) {
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Typography align="center" className="bg-(--mui-palette-background-section)" sx={{
        padding: "12px 40px"
      }}>Point-in-time recovery backup policy.</Typography>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  return null;
};